import React, { useState } from "react";
import { IoWarning, IoCheckmarkCircle } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";
import { problemSolutionData } from "../Data";

const ProblemSolution = () => {
  const [activeTab, setActiveTab] = useState(0);

  const current = problemSolutionData[activeTab];

  // Container to stagger initial section reveal
  const leftContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1,
      },
    },
  };

  // Standard vertical fade up
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 25,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  // Clean fade for cards on tab switch
  const cardVariant = {
    initial: {
      opacity: 0,
      y: 20,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
    exit: {
      opacity: 0,
      y: -15,
      transition: {
        duration: 0.25,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  return (
    <section className="problem-solution-section">
      <div className="container">

        <div className="problem-wrapper">

          {/* LEFT */}
          <motion.div
            className="problem-left"
            variants={leftContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
          >

            <motion.p
              className="section-subheading"
              variants={fadeUp}
            >
              Four problems. Four fixes.
            </motion.p>

            <motion.h2
              className="section-heading"
              variants={fadeUp}
            >
              <span>One Platform.</span>
            </motion.h2>

            {/* Tabs */}
            <motion.div
              className="problem-tabs"
              variants={fadeUp}
            >
              {problemSolutionData.map((item, index) => (
                <button
                  key={item.id}
                  className={`tab-btn ${
                    activeTab === index ? "active" : ""
                  }`}
                  onClick={() => setActiveTab(index)}
                >
                  {activeTab === index && (
                    <motion.div
                      layoutId="activeTab"
                      className="active-pill"
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 30,
                      }}
                    />
                  )}
                  <span>{item.tab}</span>
                </button>
              ))}
            </motion.div>

            {/* Problem Card */}
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id + "-problem"}
                className="problem-card"
                variants={cardVariant}
                initial="initial"
                animate="animate"
                exit="exit"
                whileHover={{ y: -6 }}
                transition={{ duration: 0.2 }}
              >
                <div className="card-label problem-label">
                  <IoWarning />
                  <span>{current.problem.title}</span>
                </div>

                <h3 className="card-heading">
                  {current.problem.heading}
                </h3>

                <p className="section-text">
                  {current.problem.description}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Solution Card */}
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id + "-solution"}
                className="solution-card"
                variants={cardVariant}
                initial="initial"
                animate="animate"
                exit="exit"
                whileHover={{ y: -6 }}
                transition={{ duration: 0.2 }}
              >
                <div className="card-label solution-label">
                  <IoCheckmarkCircle />
                  <span>{current.solution.title}</span>
                </div>

                <h3 className="card-heading">
                  {current.solution.heading}
                </h3>

                <p className="section-text">
                  {current.solution.description}
                </p>
              </motion.div>
            </AnimatePresence>

          </motion.div>

          {/* RIGHT IMAGE */}
          <motion.div
            className="problem-right"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={current.id}
                src={current.image}
                alt={`${current.tab} | Onbbits`}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.35, ease: "easeInOut" }}
              />
            </AnimatePresence>
          </motion.div>

        </div>

      </div>
    </section>
  );
};

export default ProblemSolution;